import { useCallback, useEffect, useRef, useState } from "react";
import { healthSummaryService } from "@/services/health-summary.service";
import { HealthSummaryDto } from "@/types/health-summary";

export function useHealthSummary(memberId: string) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [summary, setSummary] = useState<HealthSummaryDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasExistingSummary, setHasExistingSummary] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  // 🟢 On mount: preload any existing summary
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    healthSummaryService
      .getByMember(memberId)
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setSummary(data[0]);
          setHasExistingSummary(!data[0].regenerate);
        } else {
          setHasExistingSummary(false);
        }
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message || "Failed to fetch summary");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  // 🔁 Polling function (used only when generating)
  const pollSummary = useCallback(async () => {
    try {
      const data = await healthSummaryService.getByMember(memberId);
      if (Array.isArray(data) && data.length > 0) {
        setSummary(data[0]);
        setGenerating(false);
        setLoading(false);
        return true;
      }
      return false;
    } catch (e: any) {
      setError(e.message || "Failed to fetch summary");
      setGenerating(false);
      setLoading(false);
      return false;
    }
  }, [memberId]);

  // Triggered when user clicks “Generate Summary”
  const startGeneration = useCallback(async () => {
    setError(null);
    setLoading(true);

    // If summary already exists → just show it instantly, no backend call
    if (hasExistingSummary && summary) {
      console.log("summary exists");
      setGenerating(false);
      setLoading(false);
      return;
    }

    // Otherwise: call backend to start generation
    setGenerating(true);
    setSummary(null);
    try {
      console.log("generating summary");
      await healthSummaryService.generate(memberId);

      // Start polling every 2.5s (up to 30 attempts)
      let attempts = 0;
      const poll = async () => {
        const found = await pollSummary();
        attempts++;
        if (!found && attempts < 10) {
          pollRef.current = setTimeout(poll, 2500);
        } else {
          setGenerating(false);
        }
      };
      poll();
    } catch (e: any) {
      setError(e.message || "Failed to start summary generation");
      setGenerating(false);
      setLoading(false);
    }
  }, [memberId, pollSummary, hasExistingSummary, summary]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  // PDF/fileKey helpers
  const downloadHealthSummaryPdf = useCallback(async () => {
    return await healthSummaryService.downloadHealthSummaryPdf(memberId);
  }, [memberId]);

  const getHealthSummaryPdfUrl = useCallback(async () => {
    const blob = await downloadHealthSummaryPdf();
    return window.URL.createObjectURL(blob);
  }, [downloadHealthSummaryPdf]);

  return {
    loading,
    generating,
    summary,
    error,
    startGeneration,
    hasExistingSummary,
    setHasExistingSummary,
    downloadHealthSummaryPdf,
    getHealthSummaryPdfUrl,
  };
}
